import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChatCenteredDots, Lightbulb, Bug, Sparkle, Heart, WarningCircle, Trash, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  suggestion:      { label: 'Suggestion', icon: Lightbulb, color: 'text-amber-400' },
  bug:             { label: 'Bug',         icon: Bug,       color: 'text-red-400'   },
  feature_request: { label: 'Feature',     icon: Sparkle,   color: 'text-blue-400'  },
  complaint:       { label: 'Complaint',   icon: WarningCircle, color: 'text-orange-400' },
  praise:          { label: 'Praise',      icon: Heart,     color: 'text-blue-400'  },
  other:           { label: 'Other',       icon: ChatCenteredDots, color: 'text-slate-400' },
};

const STATUSES = ['open', 'planned', 'in_progress', 'shipped', 'wont_do', 'duplicate'];

const FeedbackAdminTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [replyDraft, setReplyDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = filterStatus ? { status: filterStatus } : {};
      const res = await axios.get(`${API}/api/feedback/admin/list`, { params });
      setItems(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);

  const updateItem = async (id, patch) => {
    try {
      await axios.patch(`${API}/api/feedback/${id}`, patch);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const saveReply = async (id) => {
    const text = replyDraft[id];
    if (!text?.trim()) { toast.error('Write something first'); return; }
    await updateItem(id, { admin_response: text });
    setReplyDraft(prev => ({ ...prev, [id]: '' }));
    toast.success('Reply sent');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await axios.delete(`${API}/api/feedback/${id}`);
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="feedback-admin-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ChatCenteredDots weight="fill" className="w-5 h-5 text-blue-400" />
            Feedback Queue
          </h2>
          <p className="text-sm text-slate-500">{items.length} item{items.length !== 1 && 's'} · Mark items public to surface them on the user-facing roadmap.</p>
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white capitalize"
          data-testid="feedback-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <div className="glass-light rounded-2xl p-12 text-center">
          <ChatCenteredDots className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">No feedback yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(it => {
            const cat = CATEGORY_META[it.category] || CATEGORY_META.other;
            const Icon = cat.icon;
            return (
              <div key={it.id} className="glass-light rounded-xl p-4" data-testid={`admin-fb-${it.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Icon weight="fill" className={`w-4 h-4 ${cat.color}`} />
                      <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{cat.label}</span>
                      <select value={it.status}
                        onChange={e => updateItem(it.id, { status: e.target.value })}
                        className="bg-slate-800 border border-white/10 rounded px-2 py-0.5 text-xs text-white capitalize"
                        data-testid={`status-${it.id}`}>
                        {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                      </select>
                      <button
                        onClick={() => updateItem(it.id, { is_public: !it.is_public })}
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${
                          it.is_public ? 'bg-green-500/20 text-green-400' : 'bg-slate-800 text-slate-500'
                        }`}
                        data-testid={`public-toggle-${it.id}`}>
                        {it.is_public ? <Eye className="w-3 h-3" /> : <EyeSlash className="w-3 h-3" />}
                        {it.is_public ? 'Public' : 'Private'}
                      </button>
                      <span className="text-xs text-slate-600 ml-auto">· {it.upvotes} upvote{it.upvotes !== 1 && 's'}</span>
                    </div>
                    <p className="text-white font-semibold mt-2">{it.subject}</p>
                    <p className="text-sm text-slate-400 mt-1 whitespace-pre-line">{it.body}</p>
                    <p className="text-xs text-slate-600 mt-2">
                      from {it.user_name} ({it.user_email}) · {new Date(it.created_at).toLocaleString()}
                    </p>
                    {it.admin_response && (
                      <div className="mt-3 p-3 bg-blue-500/5 border-l-2 border-blue-500/50 rounded">
                        <p className="text-xs uppercase tracking-wider font-bold text-blue-400 mb-1">Your reply</p>
                        <p className="text-sm text-slate-200 whitespace-pre-line">{it.admin_response}</p>
                      </div>
                    )}
                    <div className="mt-3 flex gap-2">
                      <textarea
                        value={replyDraft[it.id] ?? it.admin_response ?? ''}
                        onChange={e => setReplyDraft(prev => ({ ...prev, [it.id]: e.target.value }))}
                        placeholder="Reply (visible to the user)…"
                        rows={2}
                        className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600"
                        data-testid={`reply-${it.id}`}
                      />
                      <button onClick={() => saveReply(it.id)}
                        className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 self-start"
                        data-testid={`save-reply-${it.id}`}>
                        Reply
                      </button>
                    </div>
                  </div>
                  <button onClick={() => remove(it.id)} className="p-1.5 text-slate-500 hover:text-red-400 rounded-lg">
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeedbackAdminTab;
